import { logout, meCached, clearMeCache, patchMeCache } from "@/services/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { createContext, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import {
  requestUserEmailToken,
  requestUserWhatsappToken,
  updateAmbientService,
} from "@/services/user";
import { confirmUserBeforeTrigger } from "@/utils/confirmUserBeforeTrigger";
import { isImpersonationAccessError } from "./impersonationSession";
import { getMissingValidationsFromDetail, getVerificationStatus } from "./registrationCompletion";
import { SESSION_CREDENTIALS_MISSING_EVENT } from "./sessionCredentials";
import { clearSession, setSession } from "./utils";

const VERIFICATION_STATUS_KEY = ["verificationStatus"];
const VERIFY_MODAL_OPEN_KEY = ["isOpenVerifyModal"];

const initialState = {
  user: null,
  isAuthenticated: false,
  isInitialized: false,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      ...state,
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === "SET_USER") {
    return {
      ...state,
      isInitialized: true,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === "SET_VIEW_PAGE_AS") {
    return {
      ...state,
      user: {
        ...state.user,
        viewPageAs: action.payload,
      },
    };
  }

  return state;
};

export const SessionContext = createContext(null);

SessionProvider.propTypes = {
  children: PropTypes.node,
};

export function SessionProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const queryClient = useQueryClient();
  const [currentPath, setCurrentPath] = useState(
    typeof window !== "undefined" ? window.location.pathname : ""
  );

  useEffect(() => {
    const updatePath = () => {
      setCurrentPath(window.location.pathname);
    };

    updatePath();
    window.addEventListener("popstate", updatePath);

    const interval = setInterval(updatePath, 100);

    return () => {
      window.removeEventListener("popstate", updatePath);
      clearInterval(interval);
    };
  }, []);

  const isPublicRoute =
    currentPath.startsWith("/mcp") ||
    currentPath.startsWith("/refund") ||
    currentPath.startsWith("/auth") ||
    currentPath.startsWith("/survey") ||
    currentPath.startsWith("/p/");

  const hasTokens =
    typeof window !== "undefined" &&
    (localStorage.getItem("accessToken") || localStorage.getItem("refreshToken"));

  const { refetch } = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await meCached();
      } catch (error) {
        return null;
      }
    },
    enabled: false,
    retry: false,
  });

  const clearVerificationCache = useCallback(() => {
    queryClient.setQueryData(VERIFY_MODAL_OPEN_KEY, false);
    queryClient.setQueryData(VERIFICATION_STATUS_KEY, null);
  }, [queryClient]);

  const openVerificationModal = useCallback(
    (verificationStatus) => {
      queryClient.setQueryData(VERIFICATION_STATUS_KEY, verificationStatus);
      queryClient.setQueryData(VERIFY_MODAL_OPEN_KEY, true);

      if (verificationStatus.isToValidateEmail) {
        requestUserEmailToken().catch(() => {});
      }
      if (verificationStatus.isToValidateWhatsapp) {
        requestUserWhatsappToken().catch(() => {});
      }
    },
    [queryClient]
  );

  const loadSessionUser = useCallback(async ({ forceUpdate = false } = {}) => {
    try {
      const user = await meCached({ forceUpdate });
      return { user, missing: null };
    } catch (error) {
      const status = error?.response?.status;
      const missing = error?.response?.data?.missing;
      const missingFromDetail = getMissingValidationsFromDetail(error?.response?.data?.detail);

      if (status === 403 && Array.isArray(missing)) {
        return { user: null, missing };
      }

      if (missingFromDetail) {
        return { user: null, missing: missingFromDetail };
      }

      throw error;
    }
  }, []);

  const logoutHandler = useCallback(async () => {
    try {
      await logout();
    } catch (e) {
      console.error("Logout error:", e);
    }
    clearSession();
    clearMeCache();
    clearVerificationCache();
    queryClient.removeQueries(["me"]);
    dispatch({
      type: "LOGOUT",
    });
  }, [dispatch, queryClient, clearVerificationCache]);

  const initialize = useCallback(
    async () => {
      try {
        let { user, missing } = await loadSessionUser();

        if (!user && !missing) {
          throw new Error("Credenciais inválidas");
        }

        let verificationStatus = getVerificationStatus(user, missing);

        if (verificationStatus.isVerificationPending) {
          const confirmed = await confirmUserBeforeTrigger({
            currentValue: { user, missing },
            shouldTrigger: ({ user: currentUser, missing: currentMissing }) =>
              getVerificationStatus(currentUser, currentMissing).isVerificationPending,
            refresh: () => loadSessionUser({ forceUpdate: true }),
          });

          user = confirmed.value.user;
          missing = confirmed.value.missing;
          verificationStatus = getVerificationStatus(user, missing);
        }

        const isRegistrationPending = !user && verificationStatus.isVerificationPending;

        if (verificationStatus.isVerificationPending && !isRegistrationPending) {
          openVerificationModal(verificationStatus);
        } else {
          clearVerificationCache();
        }

        if (user) {
          queryClient.setQueryData(["me"], user);
          dispatch({
            type: "SET_USER",
            payload: { user },
          });
        } else {
          dispatch({
            type: "SET_USER",
            payload: { user: { registrationPending: true, missing } },
          });
        }
      } catch (error) {
        clearVerificationCache();
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    },
    [queryClient, openVerificationModal, clearVerificationCache, loadSessionUser]
  );

  const loginWithTokens = useCallback(
    async ({ accessToken, refreshToken, userId = null, user: providedUser = null }) => {
      try {
        setSession({ accessToken, refreshToken });
        clearMeCache();
        clearVerificationCache();

        if (providedUser || userId) {
          const user = providedUser || { id: userId };

          queryClient.setQueryData(["me"], user);
          dispatch({
            type: "SET_USER",
            payload: { user },
          });

          return user;
        }

        const result = await refetch();

        if (!result?.data) {
          throw new Error("Credenciais inválidas");
        }

        let user = result.data;
        let verificationStatus = getVerificationStatus(user);

        if (verificationStatus.isVerificationPending) {
          const confirmed = await confirmUserBeforeTrigger({
            currentValue: user,
            shouldTrigger: (currentUser) => getVerificationStatus(currentUser).isVerificationPending,
            refresh: async () => {
              const confirmedUser = await meCached({ forceUpdate: true });
              queryClient.setQueryData(["me"], confirmedUser);
              return confirmedUser;
            },
          });

          user = confirmed.value;
          verificationStatus = getVerificationStatus(user);
        }

        if (verificationStatus.isVerificationPending) {
          openVerificationModal(verificationStatus);
        } else {
          clearVerificationCache();
        }

        dispatch({
          type: "SET_USER",
          payload: { user },
        });

        return user;
      } catch (error) {
        clearSession();
        clearMeCache();
        clearVerificationCache();
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
        throw error;
      }
    },
    [refetch, openVerificationModal, clearVerificationCache, queryClient]
  );

  const setUser = useCallback((user) => {
    patchMeCache(user);
    queryClient.setQueryData(["me"], user);

    dispatch({
      type: "SET_USER",
      payload: { user },
    });
  }, [queryClient]);

  const refreshUser = useCallback(async ({ forceUpdate = false } = {}) => {
    try {
      let user = await meCached({ forceUpdate });

      queryClient.setQueryData(["me"], user);
      dispatch({
        type: "SET_USER",
        payload: { user },
      });

      let verificationStatus = getVerificationStatus(user);

      if (!forceUpdate && verificationStatus.isVerificationPending) {
        const confirmed = await confirmUserBeforeTrigger({
          currentValue: user,
          shouldTrigger: (currentUser) => getVerificationStatus(currentUser).isVerificationPending,
          refresh: () => meCached({ forceUpdate: true }),
        });

        user = confirmed.value;
        verificationStatus = getVerificationStatus(user);
        queryClient.setQueryData(["me"], user);
        dispatch({
          type: "SET_USER",
          payload: { user },
        });
      }

      if (verificationStatus.isVerificationPending) {
        openVerificationModal(verificationStatus);
      } else {
        clearVerificationCache();
      }

      return user;
    } catch (error) {
      if (error?.response?.status === 401) {
        clearVerificationCache();
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }

      throw error;
    }
  }, [queryClient, openVerificationModal, clearVerificationCache]);

  const synchronizeSessionUser = useCallback(async () => {
    const { user, missing } = await loadSessionUser({ forceUpdate: true });
    const verificationStatus = getVerificationStatus(user, missing);

    if (user) {
      queryClient.setQueryData(["me"], user);
      dispatch({
        type: "SET_USER",
        payload: { user },
      });

      if (verificationStatus.isVerificationPending) {
        openVerificationModal(verificationStatus);
      } else {
        clearVerificationCache();
      }
    } else {
      clearVerificationCache();
      dispatch({
        type: "SET_USER",
        payload: { user: { registrationPending: true, missing } },
      });
    }

    return { user, missing };
  }, [loadSessionUser, queryClient, openVerificationModal, clearVerificationCache]);

  const closeVerificationModal = useCallback(() => {
    clearVerificationCache();
  }, [clearVerificationCache]);

  const updateAmbient = useCallback(async ({ viewPageAs }) => {
    updateAmbientService({ viewPageAs });
    patchMeCache({ viewPageAs });
    queryClient.setQueryData(["me"], (oldUser) => oldUser ? { ...oldUser, viewPageAs } : oldUser);

    dispatch({
      type: "SET_VIEW_PAGE_AS",
      payload: viewPageAs,
    });
  }, [queryClient]);

  useEffect(() => {
    if (isPublicRoute && !hasTokens) {
      if (!state.isInitialized || state.isAuthenticated) {
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
      return;
    }

    if (state.isInitialized && state.isAuthenticated) {
      return;
    }

    if (hasTokens && !state.isAuthenticated) {
      initialize();
    } else if (!isPublicRoute && !state.isInitialized) {
      initialize();
    }
  }, [initialize, isPublicRoute, hasTokens, state.isAuthenticated, state.isInitialized]);

  // A sessão só é confirmada na carga; se ela acaba com a tela aberta, cada chamada volta "sem
  // credencial". Antes de encerrar, o /user/ confirma: a rota que só aceita JWT recusa assim
  // quem entrou pelo SSO com a sessão viva. Uma confirmação por vez, porque a tela que dispara
  // várias consultas recebe várias recusas juntas.
  useEffect(() => {
    if (!state.isAuthenticated) return undefined;

    let confirming = false;

    const confirmSession = async () => {
      if (confirming) return;
      confirming = true;

      try {
        await loadSessionUser({ forceUpdate: true });
      } catch (error) {
        const status = error?.response?.status;

        if ((status === 401 || status === 403) && !isImpersonationAccessError(error)) {
          clearSession();
          clearMeCache();
          clearVerificationCache();
          queryClient.removeQueries(["me"]);
          dispatch({
            type: "LOGOUT",
          });
        }
      } finally {
        confirming = false;
      }
    };

    window.addEventListener(SESSION_CREDENTIALS_MISSING_EVENT, confirmSession);

    return () => {
      window.removeEventListener(SESSION_CREDENTIALS_MISSING_EVENT, confirmSession);
    };
  }, [state.isAuthenticated, loadSessionUser, queryClient, clearVerificationCache]);

  function formatSSOUrl(path) {
    const baseUrl = process.env.REACT_APP_SSO_BASE_URL;
    const redirectUrl = process.env.REACT_APP_SSO_REDIRECT_URL;

    if (!baseUrl || !redirectUrl) {
      console.error("Variáveis de ambiente para SSO não foram configuradas corretamente.");
      return "";
    }

    const url = new URL(`${baseUrl}/${path}/`);
    url.searchParams.set("next", redirectUrl);
    return url.toString();
  }

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      refetch,
      refreshUser,
      synchronizeSessionUser,
      setUser,
      updateAmbient,
      isAuthenticated: state.isAuthenticated,
      isInitialized: state.isInitialized,
      SSOLoginURL: formatSSOUrl("login"),
      logout: logoutHandler,
      loginWithTokens,
      closeVerificationModal,
    }),
    [state.user, state.isAuthenticated, state.isInitialized, logoutHandler, refetch, refreshUser, synchronizeSessionUser, setUser, updateAmbient, loginWithTokens, closeVerificationModal]
  );

  return <SessionContext.Provider value={memoizedValue}>{children}</SessionContext.Provider>;
}
